import { createElement, useEffect } from 'react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';
import {
  createFrontendModule,
  useApi,
  configApiRef,
} from '@backstage/frontend-plugin-api';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

// backstage-plugin-techdocs-addon-mermaid@0.26.0's own `/alpha` module never
// forwards `config`/`lightConfig`/`darkConfig` from app-config to the addon
// (only `enableZoom`/`zoomOptions` make it through) -- fixed upstream in
// 0.27.0, which was still under Yarn's supply-chain quarantine when this was
// written. This re-implements that fixed wiring locally, reusing the
// package's own publicly exported `Mermaid` component instead of its
// `/alpha` entry point.
const ConfiguredMermaidAddon = () => {
  const config = useApi(configApiRef);
  const mermaidConfig = config.getOptionalConfig('techdocs.addons.mermaid');
  const props: Record<string, unknown> = {};
  if (mermaidConfig) {
    props.lightConfig = mermaidConfig.getOptional('lightConfig');
    props.darkConfig = mermaidConfig.getOptional('darkConfig');
    props.config = mermaidConfig.getOptional('config');
    props.enableZoom = mermaidConfig.getOptionalBoolean('enableZoom') ?? false;
    const zoomConfig = mermaidConfig.getOptionalConfig('zoomOptions');
    if (zoomConfig) {
      props.zoomOptions = {
        scaleExtent: zoomConfig.getOptional('scaleExtent'),
        translateExtent: zoomConfig.getOptional('translateExtent'),
      };
    }
  }
  return createElement(Mermaid, props);
};

const FULLSCREEN_BUTTON_CLASS = 'techdocs-mermaid-fullscreen-button';

function attachFullscreenButton(diagram: HTMLElement) {
  if (diagram.querySelector(`.${FULLSCREEN_BUTTON_CLASS}`)) {
    return;
  }
  if (getComputedStyle(diagram).position === 'static') {
    diagram.style.position = 'relative';
  }
  if (!diagram.style.background) {
    diagram.style.background = '#ffffff';
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = FULLSCREEN_BUTTON_CLASS;
  button.title = 'Pantalla completa';
  button.textContent = '⛶';
  Object.assign(button.style, {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: '1',
    width: '28px',
    height: '28px',
    lineHeight: '26px',
    padding: '0',
    border: 'none',
    borderRadius: '4px',
    background: 'rgba(0, 0, 0, 0.55)',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
  });

  const updateLabel = () => {
    button.textContent = document.fullscreenElement === diagram ? '✕' : '⛶';
    button.title =
      document.fullscreenElement === diagram
        ? 'Salir de pantalla completa'
        : 'Pantalla completa';
  };
  document.addEventListener('fullscreenchange', updateLabel);

  button.addEventListener('click', () => {
    if (document.fullscreenElement === diagram) {
      document.exitFullscreen();
    } else {
      diagram.requestFullscreen?.();
    }
  });

  diagram.appendChild(button);
}

// The addon renders each diagram into a plain `div.mermaid` it creates
// itself, outside of React's control and asynchronously (after mermaid.js
// finishes rendering), so a button can't be added as a normal child -- it
// has to be appended imperatively once that div exists. `useShadowRootElements`
// doesn't pick these up reliably: its own MutationObserver callback resets
// state to `shadowRoot.firstChild`, which keeps the same object identity
// across mutations, so React bails out of re-rendering and the hook's result
// never refreshes after the diagrams are inserted. Watching the shadow root
// directly here re-scans on every mutation instead of relying on that.
//
// Reusing the diagram's own container for the browser Fullscreen API keeps
// the existing zoom/pan behavior working at full-screen size instead of
// building a separate lightbox.
const MermaidFullscreenButtons = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    const addButtons = () => {
      shadowRoot.querySelectorAll<HTMLElement>('.mermaid').forEach(diagram => {
        attachFullscreenButton(diagram);
      });
    };

    addButtons();
    const observer = new MutationObserver(addButtons);
    observer.observe(shadowRoot, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [shadowRoot]);

  return null;
};

export const techDocsMermaidAddonModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    AddonBlueprint.make({
      name: 'mermaid',
      params: {
        name: 'Mermaid',
        location: 'Content',
        component: ConfiguredMermaidAddon,
      },
    }),
    AddonBlueprint.make({
      name: 'mermaid-fullscreen',
      params: {
        name: 'MermaidFullscreen',
        location: 'Content',
        component: MermaidFullscreenButtons,
      },
    }),
  ],
});
